import {useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from 'react'
import type React from 'react'

export interface UseDisclosureOptions {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  returnFocusOnClose?: boolean
}

export interface DisclosureTriggerAriaProps {
  id: string
  'aria-expanded': boolean
  tabIndex: number
  onClick: () => void
  ref: React.RefCallback<HTMLElement>
}

export interface DisclosurePanelAriaProps {
  id: string
  'aria-labelledby'?: string
}

export const useDisclosure = (options?: UseDisclosureOptions) => {
  const {open: controlledOpen, defaultOpen = false, onOpenChange, returnFocusOnClose = true} = options ?? {}

  const id = useId()
  const triggerId = `disclosure-trigger-${id}`
  const panelId = `disclosure-panel-${id}`

  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const triggerRef = useRef<HTMLElement | null>(null)

  const setOpen = useCallback(
    (next: boolean) => {
      if (next === open) return
      if (!isControlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [open, isControlled, onOpenChange],
  )

  const toggle = useCallback(() => {
    setOpen(!open)
  }, [open, setOpen])

  // Tracked eagerly while the panel is open: by the time the close layout effect runs, React has
  // already unmounted the panel's children and the region is `inert`, so `document.activeElement`
  // is `<body>` and containment can no longer be measured.
  const focusWasInsidePanelRef = useRef(false)
  useEffect(() => {
    if (!open) return
    const updateFocusLocation = (target: EventTarget | null) => {
      const panel = document.getElementById(panelId)
      focusWasInsidePanelRef.current = panel?.contains(target as Node) ?? false
    }
    const handleFocusIn = (event: FocusEvent) => updateFocusLocation(event.target)

    updateFocusLocation(document.activeElement)
    document.addEventListener('focusin', handleFocusIn)
    return () => document.removeEventListener('focusin', handleFocusIn)
  }, [open, panelId])

  const previousOpenRef = useRef(open)
  useLayoutEffect(() => {
    if (previousOpenRef.current && !open) {
      // Only reclaim focus that the closing panel is about to destroy. Closing a panel while focus
      // sits elsewhere — a sibling accordion header, or any programmatic close — must not move it.
      const shouldReturnFocus = returnFocusOnClose && focusWasInsidePanelRef.current
      focusWasInsidePanelRef.current = false
      if (shouldReturnFocus) returnFocusToTrigger(triggerRef.current)
    }
    previousOpenRef.current = open
  }, [open, returnFocusOnClose])

  const triggerCallbackRef = useCallback((node: HTMLElement | null) => {
    triggerRef.current = node
  }, [])

  // `aria-controls` is deliberately absent: it is on this repository's prohibited-ARIA list because
  // only JAWS acts on it. `aria-expanded` on the trigger plus the panel's `aria-labelledby` back to
  // the trigger carry the relationship everywhere else.
  const getTriggerProps = useCallback(
    (): DisclosureTriggerAriaProps => ({
      id: triggerId,
      'aria-expanded': open,
      // prevent Safari from omitting buttons and links from the tab order unless macOS keyboard navigation is enabled
      tabIndex: 0,
      onClick: toggle,
      ref: triggerCallbackRef,
    }),
    [triggerId, open, toggle, triggerCallbackRef],
  )

  const getPanelProps = useCallback(
    (opts?: {labelledBy?: boolean}): DisclosurePanelAriaProps =>
      opts?.labelledBy ? {id: panelId, 'aria-labelledby': triggerId} : {id: panelId},
    [panelId, triggerId],
  )

  return useMemo(
    () => ({open, setOpen, toggle, triggerId, panelId, triggerRef, getTriggerProps, getPanelProps}),
    [open, setOpen, toggle, triggerId, panelId, getTriggerProps, getPanelProps],
  )
}

/**
 * Returns focus to the trigger synchronously, from the closing layout effect.
 *
 * Refs for the commit are already attached and `CollapsibleRegion`'s own layout effect (child
 * effects run first) has already marked the panel `inert`, so the trigger can be focused before the
 * browser paints. Deferring to `requestAnimationFrame` instead parks focus on `<body>` for a frame,
 * which drops the screen reader's position and makes the next Tab restart from the top of the page.
 */
const returnFocusToTrigger = (trigger: HTMLElement | null) => {
  if (trigger?.isConnected) {
    trigger.focus()
    return
  }

  // Nothing safe is left to focus — inventing a target would move the user somewhere they never
  // asked to go — but failing silently is what hides the bug, so say so in development.
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      'useDisclosure: the panel closed while focus was inside it, but its trigger is no longer in the document, so focus could not be returned and has fallen back to <body>. Keep the trigger mounted for as long as the panel it controls.',
    )
  }
}
